import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useFeatureFlag, Variant } from '@bonusly/who-am-i';

import { resolvePageContext } from '../lib/bizy-page-config';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { handleBizySlashKeyDown } from './use-bizy-layout';

interface UseBizyAdminLayoutOptions {
	isMobile: boolean;
}

/**
 * Encapsulates all Bizy side-panel layout concerns for admin pages.
 *
 * Mirrors useBizyLayout but adapted for admin:
 * - Uses BizyForParticipation feature flag instead of company gating
 * - No homepage auto-open (admin has no equivalent "home")
 * - No sidebar collapse (admin nav is fixed-width)
 */
export function useBizyAdminLayout({ isMobile }: UseBizyAdminLayoutOptions) {
	const isEnabled = useFeatureFlag(Variant.BizyForParticipation);
	const location = useLocation();

	const isPanelOpen = useBizySidePanelStore((s) => s.isOpen);
	const panelWidth = useBizySidePanelStore((s) => s.width);
	const isResizing = useBizySidePanelStore((s) => s.isResizing);
	const togglePanel = useBizySidePanelStore((s) => s.toggle);
	const setDefaultOpen = useBizySidePanelStore((s) => s.setDefaultOpen);
	const setPageContext = useBizyPageContextStore((s) => s.setContext);

	// Auto-open once per session when the admin layout first mounts on desktop
	useEffect(() => {
		if (!isEnabled || isMobile) return;
		setDefaultOpen(true);
	}, [isEnabled, isMobile, setDefaultOpen]);

	// Sync page context from the current admin route
	useEffect(() => {
		if (!isEnabled || isMobile) return;
		setPageContext(resolvePageContext(location.pathname));
	}, [isEnabled, isMobile, location.pathname, setPageContext]);

	// "/" toggles the panel when not typing in an input
	const handleKeyDown = useCallback((event: KeyboardEvent) => handleBizySlashKeyDown(event, togglePanel), [togglePanel]);

	useEffect(() => {
		if (!isEnabled || isMobile) return;
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isEnabled, isMobile, handleKeyDown]);

	return { isEnabled, isPanelOpen, panelWidth, isResizing, togglePanel };
}
